import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useHardDeleteReleaseMutation } from '../rtk/api/datagroupsApi';
import {
  normalizeHardDeletePlan,
  buildHardDeletePayload,
  isValidEmail,
  filterEntities,
  canExecuteRelease,
} from '../utils/release/deprecationPlan';
import { formatApiError } from '../utils/release/apiError';

/**
 * State for the hard-delete (latest release) flow. Unlike deprecate there is no
 * per-entity selection: a single `deleteEntities` toggle decides whether entity
 * items are permanently removed (true) or merely untagged (false).
 *
 * step: 'form' (collect reason + scope) → 'review' (dry-run plan) → execute.
 */
const initialState = {
  step: 'form',
  reason: '',
  deleteEntities: false,
  confirmVersion: '',
  activeTab: 'all',
  search: '',
  plan: null,
  errorMessage: '',
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return { ...initialState };
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_ERROR':
      return { ...state, errorMessage: action.message };
    case 'PLAN_READY':
      return { ...state, plan: action.plan, step: 'review', errorMessage: '' };
    case 'PLAN_STALE':
      return {
        ...state,
        step: 'form',
        plan: null,
        confirmVersion: '',
        errorMessage: action.message,
      };
    case 'GO_TO_FORM':
      return { ...state, step: 'form' };
    default:
      return state;
  }
}

/**
 * @param {object} args
 * @param {string} args.groupName
 * @param {string} args.releaseVersion
 * @param {string} args.email          authenticated user's email (deletedBy)
 * @param {boolean} args.open          modal open flag (drives reset)
 * @param {(result: object) => void} [args.onDeleted]
 * @param {() => void} [args.onClose]
 */
export function useHardDeleteFlow({ groupName, releaseVersion, email, open, onDeleted, onClose }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [hardDeleteRelease, { isLoading }] = useHardDeleteReleaseMutation();

  const trimmedEmail = (email || '').trim();

  // Reset transient state whenever the modal opens for a (new) release. Email is
  // intentionally not a dependency: a token refresh must not wipe the in-progress
  // reason or scope toggle.
  useEffect(() => {
    if (open) dispatch({ type: 'RESET' });
  }, [open, releaseVersion]);

  const setField = useCallback((field, value) => dispatch({ type: 'SET_FIELD', field, value }), []);
  const goToForm = useCallback(() => dispatch({ type: 'GO_TO_FORM' }), []);

  const runDryRun = useCallback(async () => {
    dispatch({ type: 'SET_ERROR', message: '' });
    try {
      const result = await hardDeleteRelease({
        groupName,
        releaseVersion,
        payload: buildHardDeletePayload({
          email: trimmedEmail,
          reason: state.reason,
          deleteEntities: state.deleteEntities,
        }),
        dryRun: true,
      }).unwrap();
      dispatch({ type: 'PLAN_READY', plan: normalizeHardDeletePlan(result) });
    } catch (err) {
      console.error('[HardDelete] dry-run failed', err);
      dispatch({ type: 'SET_ERROR', message: formatApiError(err, 'Dry-run failed') });
    }
  }, [
    hardDeleteRelease,
    groupName,
    releaseVersion,
    trimmedEmail,
    state.reason,
    state.deleteEntities,
  ]);

  const execute = useCallback(async () => {
    dispatch({ type: 'SET_ERROR', message: '' });
    try {
      const result = await hardDeleteRelease({
        groupName,
        releaseVersion,
        payload: buildHardDeletePayload({
          email: trimmedEmail,
          reason: state.reason,
          deleteEntities: state.deleteEntities,
          planId: state.plan?.planId,
        }),
        dryRun: false,
      }).unwrap();
      onDeleted?.(result);
      onClose?.();
    } catch (err) {
      console.error('[HardDelete] execute failed', err);
      const code = err?.data?.detail?.code;
      if (err?.status === 409 && ['STALE_DELETE_PLAN', 'DRY_RUN_REQUIRED'].includes(code)) {
        dispatch({
          type: 'PLAN_STALE',
          message: 'Release data changed or the preview expired. Run dry-run analysis again.',
        });
        return;
      }
      dispatch({ type: 'SET_ERROR', message: formatApiError(err, 'Failed to delete release') });
    }
  }, [
    hardDeleteRelease,
    groupName,
    releaseVersion,
    trimmedEmail,
    state.reason,
    state.deleteEntities,
    state.plan?.planId,
    onDeleted,
    onClose,
  ]);

  // ── Derived data ──────────────────────────────────────────────────
  const filteredEntities = useMemo(
    () =>
      filterEntities(state.plan?.entities, {
        activeTab: state.activeTab,
        search: state.search,
      }),
    [state.plan, state.activeTab, state.search]
  );

  const emailValid = isValidEmail(trimmedEmail);
  // Backend requires reason min length 3.
  const canRunDryRun = emailValid && state.reason.trim().length >= 3;
  const actionCounts = useMemo(
    () => state.plan?.actionCounts || { delete: 0, untag: 0, preservedShared: 0 },
    [state.plan]
  );
  const canExecute =
    canExecuteRelease({
      emailValid,
      confirmVersion: state.confirmVersion,
      releaseVersion,
      isLoading,
    }) && Boolean(state.plan?.planId);

  return {
    state,
    isLoading,
    setField,
    goToForm,
    runDryRun,
    execute,
    filteredEntities,
    emailValid,
    canRunDryRun,
    canExecute,
    actionCounts,
  };
}
